// apps/web/src/pages/attendance/ApprovalsPage.tsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Input } from '../../components/ui/Input'
import api from '../../lib/api'

interface LeaveRequest {
  id: string
  technicianId: string
  dateFrom: string
  dateTo: string
  notes: string | null
  status: string
  rejectionReason: string | null
  leaveType: { id: string; name: string; color: string }
  technician: { id: string; firstName: string; lastName: string }
}

export function ApprovalsPage() {
  const qc = useQueryClient()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectTarget, setRejectTarget] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data: requests = [], isLoading } = useQuery<LeaveRequest[]>({
    queryKey: ['leave-requests', 'pending'],
    queryFn: async () => (await api.get<LeaveRequest[]>('/leave-requests', { params: { status: 'pending' } })).data,
  })

  const approveMutation = useMutation({
    mutationFn: async (id: string) => api.patch(`/leave-requests/${id}/approve`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      api.patch(`/leave-requests/${id}/reject`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leave-requests'] })
      setRejectOpen(false)
      setRejectReason('')
    },
  })

  const openReject = (id: string) => { setRejectTarget(id); setRejectReason(''); setRejectOpen(true) }

  const formatDate = (d: string) => new Date(d).toLocaleDateString('it-IT')

  return (
    <div className="space-y-4">
      <PageHeader
        title="Approvazione Permessi"
        breadcrumb={[{ label: 'Presenze', href: '/presenze' }, { label: 'Richieste' }]}
      />

      {isLoading ? (
        <div className="py-10 text-center text-sm text-[var(--fc-foreground-muted)]">Caricamento...</div>
      ) : requests.length === 0 ? (
        <div className="py-12 text-center text-sm text-[var(--fc-foreground-muted)]">Nessuna richiesta pending.</div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const days = Math.ceil((new Date(req.dateTo).getTime() - new Date(req.dateFrom).getTime()) / 86400000) + 1
            return (
              <div key={req.id} className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-block h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: req.leaveType.color }} />
                  <div>
                    <p className="font-medium text-[var(--fc-foreground)]">
                      {req.technician.firstName} {req.technician.lastName}
                    </p>
                    <p className="text-sm text-[var(--fc-foreground-muted)]">
                      {req.leaveType.name} — {formatDate(req.dateFrom)} → {formatDate(req.dateTo)} ({days} giorn{days === 1 ? 'o' : 'i'})
                    </p>
                    {req.notes && <p className="text-xs text-[var(--fc-foreground-muted)] mt-1">Nota: {req.notes}</p>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" onClick={() => approveMutation.mutate(req.id)} disabled={approveMutation.isPending}>
                    <Check className="h-4 w-4 mr-1" /> Approva
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => openReject(req.id)}>
                    <X className="h-4 w-4 mr-1" /> Rifiuta
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Motivo rifiuto" size="sm">
        <div className="space-y-4">
          <Input
            label="Motivazione *"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="es. Periodo non disponibile"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>Annulla</Button>
            <Button
              variant="danger"
              disabled={!rejectReason.trim() || rejectMutation.isPending}
              onClick={() => { if (rejectTarget) rejectMutation.mutate({ id: rejectTarget, reason: rejectReason }) }}
            >
              Rifiuta
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
